import axios from 'axios';
import { onAuthStateChanged } from 'firebase/auth';
import API from '../config/api';
import { auth } from '../firebaseConfig';
import { assertViewerCanWrite } from '../utils/viewerOnly';
import { offlineMode, offlineRuntimeEnabled, canCaptureDoctor, canSyncDoctor } from './config';
import { failureState, replayCandidates } from './action';
import { sendComplaintAction } from './complaintApi';
import { firestoreQueue } from './firestoreQueue';
import { recoveryJournal } from './recoveryJournal';
import { activeOfflineDoctor } from './access';
import { profileWrite, isProfileQueued } from './profileActions';

let rows = [];
let issue = '';
let running = false;
let owner = '';
let unsubscribe = () => {};
const listeners = new Set();
const inFlight = new Set();
const recovery = new Map();
const submissions = new Map();
const lastRecoveryAttempt = new Map();
const backendConfirmed = new Set();
const emit = () => listeners.forEach(listener => listener({ rows, issue, mode: offlineMode }));
const activeDoctor = activeOfflineDoctor;
const storageError = () => { issue = 'Action tracking could not update. Keep this browser open; backend retries use the same action ID.'; emit(); };

function publishRows(items) {
  const merged = new Map(items.filter(action => !backendConfirmed.has(action.operationId))
    .map(action => [action.operationId, action]));
  recovery.forEach((action, id) => { if (!backendConfirmed.has(id)) merged.set(id, action); });
  rows = Array.from(merged.values());
  emit();
}

async function apply(action) {
  inFlight.add(action.operationId);
  try {
    const result = await sendComplaintAction(action);
    backendConfirmed.add(action.operationId);
    // Backend acknowledgement is authoritative even if this metadata write is
    // interrupted; replay will return the same receipt, without another append.
    firestoreQueue.update(action, {
      status: 'applied', attempts: action.attempts + 1, entryId: result.entry._id, appliedAt: Date.now(), lastErrorCode: '', leaseOwner: '', leaseUntil: 0,
    }, () => {});
    recovery.delete(action.operationId);
    void recoveryJournal.remove(action.operationId).catch(storageError);
    rows = rows.filter(row => row.operationId !== action.operationId);
    if (!rows.length) issue = '';
    emit();
    if (auth.currentUser?.uid === action.actorUid && activeDoctor() === action.payload.doctor_id) {
      window.dispatchEvent(new CustomEvent('offlineClinicalApplied', { detail: { patientMongoId: action.payload.patient_mongo_id } }));
    }
    return { queued: false, result };
  } catch (error) {
    const patch = failureState(error, action.attempts + 1);
    void recoveryJournal.save({ ...action, ...patch }).catch(storageError);
    firestoreQueue.update(action, patch, storageError);
    return { queued: true, result: null };
  } finally { inFlight.delete(action.operationId); }
}

export async function syncPendingActions() {
  if (running || !navigator.onLine || !auth.currentUser || !canSyncDoctor(activeDoctor())) return;
  running = true;
  try {
    const uid = auth.currentUser.uid;
    const doctorId = activeDoctor();
    // The local journal is durable and account-bound. Replay it directly through
    // the idempotent backend receipt, including actions from older builds whose
    // Firestore tracking write was rejected for a delegated doctor session.
    for (const action of Array.from(recovery.values()).slice(0, 10)) {
      if (action.actorUid !== uid || action.payload.doctor_id !== doctorId || inFlight.has(action.operationId)) continue;
      const repairedLegacyFailure = action.status === 'attention' &&
        ['QUEUE_UPLOAD_FAILED', 'SESSION_CHANGED', 'OFFLINE_SAVE_UNAVAILABLE'].includes(action.lastErrorCode);
      if ((!['pending', 'retry'].includes(action.status) && !repairedLegacyFailure) || action.nextAttemptAt > Date.now()) continue;
      if ((lastRecoveryAttempt.get(action.operationId) || 0) > Date.now() - 60000) continue;
      lastRecoveryAttempt.set(action.operationId, Date.now());
      if (auth.currentUser?.uid !== uid || activeDoctor() !== doctorId) break;
      await apply(action);
    }
    // Older builds marked delegated-session actions as terminal SESSION_CHANGED
    // after Firestore had already confirmed them. Replay those exact immutable
    // commands directly; the backend operation receipt prevents duplicates if
    // another tab recovers the same action concurrently.
    const repairedCloudActions = rows.filter(action =>
      action.actorUid === uid && action.payload?.doctor_id === doctorId && action.cloudConfirmed &&
      action.status === 'attention' &&
      ['SESSION_CHANGED', 'OFFLINE_SAVE_UNAVAILABLE'].includes(action.lastErrorCode)
    ).slice(0, 10);
    for (const action of repairedCloudActions) {
      if (auth.currentUser?.uid !== uid || activeDoctor() !== doctorId) break;
      if (!inFlight.has(action.operationId)) await apply(action);
    }
    for (const action of replayCandidates(rows, uid, doctorId)) {
      if (auth.currentUser?.uid !== uid || activeDoctor() !== doctorId) break;
      if (inFlight.has(action.operationId)) continue;
      if (await firestoreQueue.claim(action, owner)) {
        if (auth.currentUser?.uid !== uid || activeDoctor() !== doctorId) break;
        await apply(action);
      }
    }
  } catch { storageError(); }
  finally { running = false; }
}

export function saveComplaintWithOffline(payload) {
  const key = JSON.stringify([auth.currentUser?.uid, payload]);
  if (submissions.has(key)) return submissions.get(key);
  const pending = saveComplaint(payload).finally(() => submissions.delete(key));
  submissions.set(key, pending);
  return pending;
}

async function saveComplaint(payload) {
  // Feature off / other doctors / drain mode retain the legacy online call.
  if (!canCaptureDoctor(payload.doctor_id) || activeDoctor() !== payload.doctor_id) {
    const response = await axios.post(`${API.BASE_URL}/api/patients/complaint-history`, payload);
    return { queued: false, result: response.data };
  }
  assertViewerCanWrite();
  if (!auth.currentUser?.uid) throw new Error('Sign in before saving offline clinical entries.');
  // The profile journal supports direct doctors and verified delegated sessions.
  // Existing online saves still take the unchanged API path inside profileWrite;
  // disconnected saves are durable in IndexedDB and replay through the backend.
  try {
    const response = await profileWrite('POST', `${API.BASE_URL}/api/patients/complaint-history`, payload);
    return { queued: false, result: response.data };
  } catch (error) {
    if (isProfileQueued(error)) return { queued: true, result: null };
    throw error;
  }
}

export function watchOfflineStatus(listener) {
  listeners.add(listener);
  listener({ rows, issue, mode: offlineMode });
  return () => listeners.delete(listener);
}

export function retryAction(action) {
  if (action.actorUid !== auth.currentUser?.uid || action.payload.doctor_id !== activeDoctor() || inFlight.has(action.operationId)) return;
  if (recovery.has(action.operationId)) {
    const clean = { ...action, status: 'pending', nextAttemptAt: 0, leaseOwner: '', leaseUntil: 0, lastErrorCode: '' };
    delete clean.cloudConfirmed;
    recovery.set(action.operationId, clean);
    void recoveryJournal.save(clean).then(() => {
      publishRows(rows);
      return syncPendingActions();
    }).catch(storageError);
  } else {
    firestoreQueue.update(action, { status: 'pending', nextAttemptAt: 0, leaseUntil: 0, lastErrorCode: '' }, storageError);
  }
}

export function startOfflineRuntime() {
  if (!offlineRuntimeEnabled) return () => {};
  owner = crypto.randomUUID();
  let stopped = false;
  let generation = 0;
  const connect = async () => {
    const current = ++generation;
    unsubscribe(); rows = []; issue = ''; recovery.clear(); lastRecoveryAttempt.clear(); emit();
    const uid = auth.currentUser?.uid;
    if (!uid || !canSyncDoctor(activeDoctor())) return;
    try {
      await firestoreQueue.ready();
      if (stopped || current !== generation || auth.currentUser?.uid !== uid) return;
      const local = await recoveryJournal.list(uid);
      if (stopped || current !== generation) return;
      // Restore local-only or rejected writes without trusting another account's cache.
      local.filter(action => action.payload.doctor_id === activeDoctor()).forEach(action => {
        recovery.set(action.operationId, { ...action, cloudConfirmed: false });
      });
      publishRows([]);
      unsubscribe = firestoreQueue.subscribe(uid, items => {
        if (current !== generation) return;
        // Cloud/local Firestore snapshots supersede a recovery copy once the
        // queue document is available. Missing copies stay visible for retry.
        items.filter(action => action.cloudConfirmed).forEach(action => recovery.delete(action.operationId));
        publishRows(items.filter(action => action.payload?.doctor_id === activeDoctor()));
        void syncPendingActions();
      }, () => { issue = 'Offline action tracking unavailable. Check access and connection; do not clear site data.'; emit(); });
    } catch (error) { issue = error.message; emit(); }
  };
  const authStop = onAuthStateChanged(auth, connect);
  const tick = () => { void syncPendingActions(); };
  const beforeUnload = event => {
    if (rows.some(action => !action.cloudConfirmed)) { event.preventDefault(); event.returnValue = ''; }
  };
  window.addEventListener('doctorIdChanged', connect);
  window.addEventListener('assistantIdChanged', connect);
  window.addEventListener('online', tick);
  window.addEventListener('beforeunload', beforeUnload);
  const timer = setInterval(tick, 15000);
  return () => {
    stopped = true; generation++; authStop(); unsubscribe(); clearInterval(timer);
    window.removeEventListener('doctorIdChanged', connect);
    window.removeEventListener('assistantIdChanged', connect);
    window.removeEventListener('online', tick);
    window.removeEventListener('beforeunload', beforeUnload);
    rows = []; recovery.clear(); emit();
  };
}
